import { useMemo } from 'react'
import { Box } from '@mui/material'

const tagsPermitidas = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'SPAN',
  'FONT',
  'DIV',
  'P',
  'BR',
  'UL',
  'OL',
  'LI',
])

// Aplicativos como Word, Pages e Mail podem incluir folhas de estilo junto
// ao fragmento copiado. Elas não fazem parte do texto e jamais devem ser
// transformadas em conteúdo visível.
const tagsDescartadas = new Set([
  'STYLE',
  'SCRIPT',
  'META',
  'LINK',
  'TITLE',
  'NOSCRIPT',
])

const corPermitida = (valor) =>
  /^#[0-9a-f]{3,8}$/i.test(valor) ||
  /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(valor)

const escapaHtml = (valor) =>
  valor.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const removeFolhaDeEstilosColada = (valor = '') =>
  String(valor).replace(
    /^\s*(?:(?:[a-z][\w-]*(?:[.#][\w-]+)*\s*)\{[^{}]*\}\s*)+/i,
    '',
  )

export const sanitizaHtml = (html = '') => {
  const conteudo = String(html)
  const origem = document.createElement('template')
  origem.innerHTML = /<\/?[a-z][\s\S]*>/i.test(conteudo)
    ? conteudo
    : escapaHtml(conteudo).replace(/\n/g, '<br>')

  const destino = document.createElement('div')
  const estilosDasClasses = new Map()

  Array.from(origem.content.querySelectorAll('style')).forEach((folha) => {
    const regras = folha.textContent.matchAll(/\.([\w-]+)\s*\{([^}]*)\}/g)
    Array.from(regras).forEach(([, nomeClasse, declaracoes]) => {
      const estilo = document.createElement('span').style
      estilo.cssText = declaracoes
      estilosDasClasses.set(nomeClasse, {
        cor: estilo.color,
        negrito:
          estilo.fontWeight === 'bold' || Number(estilo.fontWeight) >= 600,
        italico: estilo.fontStyle === 'italic',
        sublinhado: estilo.textDecoration.toLowerCase().includes('underline'),
      })
    })
  })

  const copiaNo = (no, pai) => {
    if (no.nodeType === Node.TEXT_NODE) {
      pai.appendChild(
        document.createTextNode(removeFolhaDeEstilosColada(no.textContent)),
      )
      return
    }

    if (no.nodeType !== Node.ELEMENT_NODE) return

    if (tagsDescartadas.has(no.tagName)) return

    if (!tagsPermitidas.has(no.tagName)) {
      Array.from(no.childNodes).forEach((filho) => copiaNo(filho, pai))
      return
    }

    const elemento = document.createElement(
      no.tagName === 'FONT' ? 'span' : no.tagName.toLowerCase(),
    )
    const estiloDaClasse = Array.from(no.classList || [])
      .map((nomeClasse) => estilosDasClasses.get(nomeClasse))
      .filter(Boolean)
      .pop()
    const cor =
      no.style.color || no.getAttribute('color') || estiloDaClasse?.cor
    if (cor && corPermitida(cor)) elemento.style.color = cor
    if (no.style.fontWeight === 'normal') elemento.style.fontWeight = 'normal'
    if (no.style.fontStyle === 'normal') elemento.style.fontStyle = 'normal'
    if (no.style.textDecoration === 'none')
      elemento.style.textDecoration = 'none'
    if (
      no.tagName !== 'B' &&
      no.tagName !== 'STRONG' &&
      (no.style.fontWeight === 'bold' ||
        Number(no.style.fontWeight) >= 600 ||
        estiloDaClasse?.negrito)
    )
      elemento.style.fontWeight = 'bold'
    if (
      no.tagName !== 'I' &&
      no.tagName !== 'EM' &&
      (no.style.fontStyle === 'italic' || estiloDaClasse?.italico)
    )
      elemento.style.fontStyle = 'italic'
    if (
      no.tagName !== 'U' &&
      (no.style.textDecoration.toLowerCase().includes('underline') ||
        estiloDaClasse?.sublinhado)
    )
      elemento.style.textDecoration = 'underline'

    Array.from(no.childNodes).forEach((filho) => copiaNo(filho, elemento))
    pai.appendChild(elemento)
  }

  Array.from(origem.content.childNodes).forEach((no) => copiaNo(no, destino))
  return destino.innerHTML
}

const FormattedHtml = ({ html, sx }) => {
  const conteudoSeguro = useMemo(() => sanitizaHtml(html), [html])

  return (
    <Box
      component="div"
      sx={{
        '& p, & div': { my: 0.25 },
        '& ul, & ol': { my: 0.5, pl: 2.5 },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: conteudoSeguro }}
    />
  )
}

export default FormattedHtml
